import { Permission } from '@act/shared/authorization';
import {
  AddressDto,
  EmailAddressDto,
  FilterDto,
  FilterGroupDto,
  MemberStatusDto,
  MemberStatusSelectOption,
  PaginatedUsersDto,
  PhoneNumberDto,
  UserDto,
  UserForGeoMapDto,
} from '@act/shared/data-transfer-objects';
import {
  EmailAddressWSPayload,
  EmailEventTypes,
  UserEventTypes,
  UserUpdatedEventPayload,
} from '@act/shared/socket-events';
import {
  EntityId,
  PayloadAction,
  createAction,
  createEntityAdapter,
  createSlice,
  current,
} from '@reduxjs/toolkit';
import { selectMember, selectSelectedMemberId } from './selectedMemberSlice';
import { addToEffectsMiddleware } from 'redux/utils/effects-middleware';
import { createSelector } from 'reselect';
import { membersRequests } from '../requests/members.request';
import { userRequests } from '../requests/users.request';
import { useAppSelector } from '../store-hooks';
import type { RootState } from '../reemaStore';
import { MemberState } from '../types/membersTypes';
import { createApiThunk } from '../utils/api-thunk';
import type { WebsocketEventActions } from '../types/websocket-types';
import { getClientById, selectClientById } from './clientSlice';
import { userIsMember } from '@act/shared/definitions';
import { checkPermissionOnMember } from 'redux/utils/auth/permissionChecks';
import { summarizeRawEthnicities } from 'redux/utils/race-ethnicity-parser';
import parseInteractableResponses from 'redux/utils/interactableResponseParser';
// Import shared thunks to avoid circular dependencies
import { getCalls } from '../thunks/calls.thunks';
import { getUsersByIds } from '../thunks/users.thunks';
import { getSMSMessages } from './smsMessagesSlice';
import { resetRedux } from '../actions/shared-actions';
import { verifyUser } from './actingUserSlice';
import {
  hospitalizationsActions,
  getCurrentAndPendingHospitalizations,
} from './hospitalizations.slice';
import { getExpiringAndNeedsActionUserNeedsForGuideSuccess } from './needs.slice';

// THUNKS/ACTIONS ==============================================================
export const getMember = createApiThunk(membersRequests.getMember);
export const getMemberStatuses = createApiThunk(
  membersRequests.getMemberStatuses
);
export const getMembersForMap = createApiThunk(
  membersRequests.getMembersForMap
);
export const getPaginatedMembersForTable = createApiThunk(
  membersRequests.getPaginatedMembersForTable
);
export const getMemberFilters = createApiThunk(
  membersRequests.getMemberFilters
);
type NewMembersPayload = { members: UserDto[] };
export const newMembersLoaded = createAction<NewMembersPayload>(
  'members/newMembersLoaded'
);

export const getMemberInfo = createApiThunk(userRequests.getUserDetails);

export const patchMemberInfo = createApiThunk(userRequests.patchMemberInfo);
export const putRaceEthnicityInfo = createApiThunk(
  userRequests.putRaceEthnicityInfo
);

export const createPhoneNumber = createApiThunk(userRequests.createPhoneNumber);
export const deletePhoneNumber = createApiThunk(userRequests.deletePhoneNumber);
export const updatePhoneNumber = createApiThunk(userRequests.updatePhoneNumber);

export const createAddress = createApiThunk(userRequests.createAddress);
export const deleteAddress = createApiThunk(userRequests.deleteAddress);
export const updateAddress = createApiThunk(userRequests.updateAddress);
export const updatePrimaryAddress = createApiThunk(
  userRequests.updatePrimaryAddress
);

export const updatedPrimaryAddressSuccess = createAction<{
  userId: string;
  address: AddressDto;
}>('member/updatedPrimaryAddressSuccess');

export const updatedAddressSuccess = createAction<{
  userId: string;
  addressId: number;
  address: AddressDto;
}>('member/updatedAddressSuccess');

export const createAddressSuccess = createAction<{
  userId: string;
  address: AddressDto;
}>('member/createAddressSuccess');

export const deletedAddressSuccess = createAction<{
  userId: string;
  addressId: number;
}>('member/deletedAddressSuccess');

export const createEmailAddressSuccess = createAction<{
  memberId: string;
  emailAddress: EmailAddressDto;
}>('member/createEmailAddressSuccess');

export const updatedEmailAddressSuccess = createAction<{
  memberId: string;
  emailAddressId: number;
  emailAddress: EmailAddressDto;
}>('member/updatedEmailAddressSuccess');

export const deletedEmailAddressSuccess = createAction<{
  memberId: string;
  emailAddressId: number;
}>('member/deletedEmailAddressSuccess');

export const updatedPrimaryPhoneNumberSuccess = createAction<{
  userId: string;
  phoneNumber: PhoneNumberDto;
}>('member/updatedPrimaryPhoneNumberSuccess');

export const createPhoneNumbeUserSuccess = createAction<{
  userId: string;
  phoneNumber: PhoneNumberDto;
}>('member/createPhoneNumberSuccess');

export const updatedPhoneNumberUserSuccess = createAction<{
  userId: string;
  phoneNumberId: number;
  phoneNumber: PhoneNumberDto;
}>('member/updatedPhoneNumberSuccess');

// INITIAL STATE ===============================================================
export const memberEntityAdapter = createEntityAdapter<UserDto>();
export const memberForMapEntityAdapter =
  createEntityAdapter<UserForGeoMapDto>();
export const memberStatusEntityAdapter = createEntityAdapter<MemberStatusDto>();
export const paginatedMemberEntityAdapter = createEntityAdapter<UserDto>();
export const filterEntityAdapter = createEntityAdapter<FilterDto, EntityId>({
  selectId: filter => filter.key,
});

const initialState: MemberState = {
  status: 'initial',
  members: memberEntityAdapter.getInitialState(),
  membersForMap: memberForMapEntityAdapter.getInitialState(),
  paginatedMembers: paginatedMemberEntityAdapter.getInitialState(),
  memberStatuses: memberStatusEntityAdapter.getInitialState(),
  count: null,
  filters: [],
};

const addMembersOnly = (state: MemberState, users: UserDto[]) => {
  const toAdd = [];
  for (const user of users) {
    if (userIsMember(user)) {
      toAdd.push(user);
    }
  }
  memberEntityAdapter.addMany(state.members, toAdd);
};

// SLICE =======================================================================
export const memberSlice = createSlice({
  name: 'member',
  initialState,
  reducers: {
    emailDeleted: (state, action: PayloadAction<EmailAddressWSPayload>) => {
      const { emailAddress, memberId } = action.payload;
      const member = state.members.entities[memberId];
      if (!member) {
        return state;
      }

      const emailAddressesMinusDeleted = member.emailAddresses.filter(
        ea => ea.id !== emailAddress.id
      );

      state.members.entities[memberId] = {
        ...member,
        emailAddresses: [...emailAddressesMinusDeleted],
      };

      return state;
    },
    userUpdated: (state, action: PayloadAction<UserUpdatedEventPayload>) => {
      // this websocket update doesn't grab the permissions for each acting user
      // so we should preserve it from the existing state if there is one
      const originalUser = state.members.entities[action.payload.user.id];
      const updatedUser = {
        ...action.payload.user,
        memberPermissions: originalUser?.memberPermissions || [],
        userPermissions: originalUser?.userPermissions || [],
      };
      memberEntityAdapter.upsertOne(state.members, updatedUser);
    },
  },
  extraReducers: builder => {
    builder

      .addCase(getMember.fulfilled, (state, action: PayloadAction<UserDto>) => {
        state.status = 'success';
        memberEntityAdapter.upsertOne(state.members, action.payload);
      })

      // Get Members for Map =====
      .addCase(getMembersForMap.pending, state => {
        state.status = 'loading';
        memberForMapEntityAdapter.removeAll(state.membersForMap);
      })
      .addCase(
        getMembersForMap.fulfilled,
        (state, action: PayloadAction<UserForGeoMapDto[]>) => {
          state.status = 'success';
          memberForMapEntityAdapter.addMany(
            state.membersForMap,
            action.payload
          );
        }
      )
      .addCase(getMembersForMap.rejected, state => {
        state.status = 'failed';
      })

      // Get Paginated Members for Table ====
      .addCase(getPaginatedMembersForTable.pending, state => {
        state.status = 'loading';
        paginatedMemberEntityAdapter.removeAll(state.paginatedMembers);
      })
      .addCase(
        getPaginatedMembersForTable.fulfilled,
        (state, action: PayloadAction<PaginatedUsersDto>) => {
          state.status = 'success';
          state.count = action.payload.count;
          paginatedMemberEntityAdapter.addMany(
            state.paginatedMembers,
            action.payload.users
          );
        }
      )
      .addCase(getPaginatedMembersForTable.rejected, state => {
        state.status = 'failed';
      })

      // Get Member Filters ====
      .addCase(getMemberFilters.pending, state => {
        state.status = 'loading';
        state.filters = [];
      })
      .addCase(
        getMemberFilters.fulfilled,
        (state, action: PayloadAction<FilterGroupDto>) => {
          state.status = 'success';
          state.filters = action.payload.filters;
        }
      )
      .addCase(getMemberFilters.rejected, state => {
        state.status = 'failed';
      })

      .addCase(getMemberStatuses.pending, state => {
        state.status = 'loading';
      })
      .addCase(getMemberStatuses.fulfilled, (state, action) => {
        state.status = 'success';
        memberStatusEntityAdapter.addMany(state.memberStatuses, action.payload);
      })
      .addCase(getUsersByIds.fulfilled, (state, action) => {
        addMembersOnly(state, action.payload.users);
      })
      .addCase(getCalls.fulfilled, (state, action) => {
        addMembersOnly(state, action.payload.users);
      })
      .addCase(getSMSMessages.fulfilled, (state, action) => {
        addMembersOnly(state, action.payload.users);
      })
      .addCase(
        getCurrentAndPendingHospitalizations.fulfilled,
        (state, action) => {
          // Extract members from hospitalizations
          const members = action.payload.allHospitalizations
            .map(h => h.member)
            .filter((m): m is UserDto => m !== null && m !== undefined);
          addMembersOnly(state, members);
        }
      )

      .addCase(newMembersLoaded, (state, action) => {
        memberEntityAdapter.upsertMany(state.members, action.payload.members);
      })

      // Member info ======
      .addCase(resetRedux, state => {
        state = { ...initialState };
        return state;
      })
      .addCase(getMemberInfo.fulfilled, (state, action) => {
        memberEntityAdapter.upsertOne(state.members, action.payload);
      })
      .addCase(patchMemberInfo.fulfilled, (state, action) => {
        memberEntityAdapter.upsertOne(state.members, action.payload);
      })
      .addCase(putRaceEthnicityInfo.fulfilled, (state, action) => {
        const member = state.members.entities[action.meta.arg.params.memberId];
        if (member) {
          member.raceEthnicitySummary = summarizeRawEthnicities(action.payload);
          memberEntityAdapter.upsertOne(state.members, member);
        }
      })
      .addCase(createPhoneNumber.fulfilled, (state, action) => {
        const member = state.members.entities[action.meta.arg.params.userId];
        if (member) {
          member.phoneNumbers = [...member.phoneNumbers, action.payload];
          memberEntityAdapter.upsertOne(state.members, member);
        }
      })
      .addCase(createPhoneNumbeUserSuccess, (state, action) => {
        const member = state.members.entities[action.payload.userId];
        if (member) {
          member.phoneNumbers = [
            ...member.phoneNumbers,
            action.payload.phoneNumber,
          ];
          memberEntityAdapter.upsertOne(state.members, member);
        }
      })
      .addCase(updatedPhoneNumberUserSuccess, (state, action) => {
        const member = state.members.entities[action.payload.userId];
        if (member) {
          member.phoneNumbers = member.phoneNumbers.map(phoneNumber => {
            if (
              action.payload.phoneNumber.primary &&
              phoneNumber.id !== action.payload.phoneNumberId
            ) {
              return { ...phoneNumber, primary: false };
            }
            if (phoneNumber.id === action.payload.phoneNumberId) {
              return action.payload.phoneNumber;
            }
            return phoneNumber;
          });
          memberEntityAdapter.upsertOne(state.members, member);
        }
      })
      .addCase(deletePhoneNumber.fulfilled, (state, action) => {
        const member = state.members.entities[action.meta.arg.params.userId];
        if (member) {
          member.phoneNumbers = [...member.phoneNumbers].filter(
            pn => current(pn).id !== action.payload.id
          );
          memberEntityAdapter.upsertOne(state.members, member);
        }
      })
      .addCase(updatePhoneNumber.fulfilled, (state, action) => {
        const member = state.members.entities[action.meta.arg.params.userId];
        if (member) {
          member.phoneNumbers = parseInteractableResponses(action.payload, [
            ...member.phoneNumbers,
          ] as PhoneNumberDto[]);
          memberEntityAdapter.upsertOne(state.members, member);
        }
      })
      .addCase(createAddressSuccess, (state, action) => {
        const member = state.members.entities[action.payload.userId];
        if (member) {
          member.addresses = [...member.addresses, action.payload.address];
          memberEntityAdapter.upsertOne(state.members, member);
        }
      })
      .addCase(updatedAddressSuccess, (state, action) => {
        const member = state.members.entities[action.payload.userId];
        if (member && member.addresses) {
          // Mark the old address as deleted instead of removing it
          const addressIndex = member.addresses.findIndex(
            address => address.id === action.payload.addressId
          );
          if (addressIndex !== -1) {
            member.addresses[addressIndex].deletedAt = Date.now();
          }
          // Add the new address
          member.addresses = [...member.addresses, action.payload.address];
          memberEntityAdapter.upsertOne(state.members, member);
        }
      })
      .addCase(deletedAddressSuccess, (state, action) => {
        const member = state.members.entities[action.payload.userId];
        if (member && member.addresses) {
          // Mark the address as deleted instead of removing it
          const addressIndex = member.addresses.findIndex(
            address => address.id === action.payload.addressId
          );
          if (addressIndex !== -1) {
            member.addresses[addressIndex].deletedAt = Date.now();
          }
          memberEntityAdapter.upsertOne(state.members, member);
        }
      })
      .addCase(verifyUser.fulfilled, (state, action) => {
        memberStatusEntityAdapter.addMany(
          state.memberStatuses,
          action.payload.memberStatuses
        );
      })
      .addCase(hospitalizationsActions.gotHospitalizations, (state, action) => {
        const members = action.payload.data.map(h => h.member);
        memberEntityAdapter.addMany(state.members, members);
      })
      .addCase(updatedPrimaryAddressSuccess, (state, action) => {
        const member = state.members.entities[action.payload.userId];
        if (member) {
          member.addresses = member.addresses.map(address => {
            if (address.id === action.payload.address.id) {
              return action.payload.address;
            }
            return { ...address, primary: false };
          });
          memberEntityAdapter.upsertOne(state.members, member);
        }
      })
      .addCase(createEmailAddressSuccess, (state, action) => {
        const member = state.members.entities[action.payload.memberId];
        if (member) {
          member.emailAddresses = [
            ...member.emailAddresses,
            action.payload.emailAddress,
          ];
          memberEntityAdapter.upsertOne(state.members, member);
        }
      })
      .addCase(updatedEmailAddressSuccess, (state, action) => {
        const member = state.members.entities[action.payload.memberId];
        if (member) {
          member.emailAddresses = member.emailAddresses.map(emailAddress => {
            if (emailAddress.id === action.payload.emailAddressId) {
              return action.payload.emailAddress;
            }
            if (action.payload.emailAddress.primary) {
              return { ...emailAddress, primary: false };
            }
            return emailAddress;
          });
          memberEntityAdapter.upsertOne(state.members, member);
        }
      })
      .addCase(deletedEmailAddressSuccess, (state, action) => {
        const member = state.members.entities[action.payload.memberId];
        if (member) {
          member.emailAddresses = member.emailAddresses.filter(
            emailAddress => emailAddress.id !== action.payload.emailAddressId
          );
          memberEntityAdapter.upsertOne(state.members, member);
        }
      });
  },
});

// REDUCER =====================================================================
export const memberReducer = memberSlice.reducer;

// SELECTORS ===================================================================
interface MemberSliceSelectorProps {
  guideId?: string;
  memberStageId?: number;
  memberStatusId?: number;
  [key: string]: unknown;
}

const selectMembersSliceState = state =>
  (state as { members: typeof initialState })?.members;

export const selectMembersState = state =>
  (state as { members: typeof initialState })?.members?.members;

const selectMemberStatusesState = state =>
  (state as { members: typeof initialState })?.members?.memberStatuses;

const selectPaginatedMembersState = state =>
  (state as { members: typeof initialState })?.members?.paginatedMembers;

const selectMembersForMapState = state =>
  (state as { members: typeof initialState })?.members?.membersForMap;

const selectPaginatedMemberCount = createSelector(
  selectMembersSliceState,
  membersState => membersState.count
);

const selectSliceStatus = createSelector(
  selectMembersSliceState,
  membersState => membersState.status
);

export const { selectAll: selectAllMembers, selectTotal: selectMembersTotal } =
  memberEntityAdapter.getSelectors(selectMembersState);

export const {
  selectAll: selectAllPaginatedMembers,
  selectTotal: selectPaginatedMembersTotal,
} = paginatedMemberEntityAdapter.getSelectors(selectPaginatedMembersState);

export const {
  selectAll: selectAllMembersForMap,
  selectTotal: selectedTotalMembersForMap,
} = memberForMapEntityAdapter.getSelectors(selectMembersForMapState);

const { selectAll: selectAllMemberStatuses } =
  memberStatusEntityAdapter.getSelectors(selectMemberStatusesState);

const selectMembersMapByInteractableId = createSelector(
  selectAllMembers,
  allMembers => {
    const map: { [key: string]: UserDto } = {};
    allMembers.forEach(m => {
      map[m.interactableId] = m;
    });
    return map;
  }
);

const selectMemberStatusById = (id: number) =>
  createSelector(selectMemberStatusesState, state => {
    return state.entities[id];
  });

export const selectMemberById = (id: string) =>
  createSelector(selectMembersState, members => {
    return members?.entities?.[id];
  });

export const selectSelectedMember = state => {
  const selectedMemberId = selectSelectedMemberId(state);
  return selectMemberById(selectedMemberId)(state);
};

interface SelectableStatusesSelectorProps {
  memberId?: string;
}

export const selectSelectableMemberStatuses: (
  state,
  props: SelectableStatusesSelectorProps
) => MemberStatusSelectOption[] = (state, props) => {
  const memberStatuses = selectAllMemberStatuses(state);
  const member = selectMemberById(props.memberId)(state);
  const client = selectClientById(member?.clientId)(state);
  const isBhClient = !!client?.avonMedicalCenterId;

  const calculateIsSelectable = (memberStatus: MemberStatusDto) => {
    const isSelectableFromCurrentStage =
      memberStatus.changeRequestStageIds?.includes(member?.memberStage?.id);
    const isSelectableFromCurrentStatus =
      memberStatus.changeRequestStatusIds?.includes(
        member?.memberStatus?.memberStatusId
      );
    const isSelectableFromBhClient =
      isBhClient && memberStatus.key === 'skill-building';
    return (
      isSelectableFromCurrentStage ||
      isSelectableFromCurrentStatus ||
      isSelectableFromBhClient
    );
  };

  return memberStatuses.map(memberStatus => {
    return {
      isSelectable: calculateIsSelectable(memberStatus),
      memberStatus,
    };
  });
};

export const selectMembersMap = createSelector(selectMembersState, members => {
  return members.entities;
});

export const hasPermissionOnMember = (
  state: RootState,
  memberId: string,
  permission: Permission
) => {
  const member: UserDto = selectMemberById(memberId)(state);
  const actingUser = state.actingUser.user;
  return checkPermissionOnMember({ actingUser, member, permission });
};

export const selectMembersByAssignedGuide: (
  state,
  props: MemberSliceSelectorProps
) => UserDto[] = createSelector(
  [selectAllMembers, (state, props: MemberSliceSelectorProps) => props.guideId],
  (members, guideId) =>
    members.filter(member => member.assignedGuideId === guideId)
);

// EXPORT HELPER ===============================================================
export const membersAppSelectors = {
  useSelectAllMembers: () => useAppSelector(selectAllMembers),
  useSelectMembersTotal: () => useAppSelector(selectMembersTotal),
  useSelectMembersMap: () => useAppSelector(selectMembersMap),
  useSelectAllPaginatedMembers: () => useAppSelector(selectAllPaginatedMembers),
  useSelectTotalPaginatedMembers: () =>
    useAppSelector(selectPaginatedMembersTotal),
  useSelectAllMembersForMap: () => useAppSelector(selectAllMembersForMap),
  useSelectedTotalMembersForMap: () =>
    useAppSelector(selectedTotalMembersForMap),
  useSelectSliceStatus: () => useAppSelector(selectSliceStatus),
  useSelectPaginatedMemberCount: () =>
    useAppSelector(selectPaginatedMemberCount),
  useSelectAllMemberStatuses: () => useAppSelector(selectAllMemberStatuses),
  useSelectMemberStatusById: (id: number) =>
    useAppSelector(selectMemberStatusById(id)),
  useSelectMemberById: id => useAppSelector(selectMemberById(id)),
  useSelectMemberByInteractableId: (id: string) =>
    useAppSelector(state => selectMembersMapByInteractableId(state)[id]),
  useSelectMembersByAssignedGuide: (id: string) =>
    useAppSelector(state =>
      selectMembersByAssignedGuide(state, { guideId: id })
    ),
  useSelectSelectableMemberStatuses: (input: SelectableStatusesSelectorProps) =>
    useAppSelector(state => selectSelectableMemberStatuses(state, input)),
  useHasPermissionOnMember: (memberId: string, permission: Permission) =>
    useAppSelector(state => hasPermissionOnMember(state, memberId, permission)),
};

// EFFECTS =====================================================================
addToEffectsMiddleware(addEffect => {
  addEffect({
    action: selectMember,
    effect: async ({ dispatch, action }) => {
      if (action.payload) {
        void dispatch(getMemberInfo({ params: { memberId: action.payload } }));
      }
    },
  });
  addEffect({
    action: getMemberInfo.fulfilled,
    effect: async ({ dispatch, action }) => {
      if (action.payload) {
        dispatch(getClientById({ params: { id: action.payload.clientId } }));
      }
    },
  });
  addEffect({
    action: getExpiringAndNeedsActionUserNeedsForGuideSuccess,
    effect: async ({ dispatch, action, state }) => {
      if (!action.payload || action.payload.length === 0) return;

      const memberMap = selectMembersMap(state);
      const missingMemberIds = action.payload
        .map(need => need.memberId)
        .filter(memberId => memberId)
        .filter(memberId => !memberMap[memberId])
        .filter((id, index, self) => self.indexOf(id) === index);

      if (missingMemberIds.length > 0) {
        void dispatch(getUsersByIds({ params: { ids: missingMemberIds } }));
      }
    },
  });
});

// WEBSOCKET ACTIONS ===========================================================
export const memberWebsocketActions: WebsocketEventActions[] = [
  {
    type: EmailEventTypes.EMAIL_ADDRESS_DELETED,
    action: memberSlice.actions.emailDeleted,
    channel: 'baseSocket',
  },
  {
    type: UserEventTypes.USER_UPDATED,
    action: memberSlice.actions.userUpdated,
    channel: 'baseSocket',
  },
];
